import React, { useState } from 'react';
import { Stack, Typography, Box, Collapse, Tooltip } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

import SectionCard from './SectionCard';
import { TableCard } from '../tabLayout';
import { ToneChip } from '../../common/StatusChip';
import { numSx } from './sectionParts';
import { humanize } from '../../../utils/pdf/format';
import { AdmissionCycle, AdmissionFormSection, AdmissionField } from '../../../types/admissions';
import FormEditorDialog from './formEditor/FormEditorDialog';
import { activeFormSections, isPhotoField } from '../form/admissionFormFields';

interface Props {
  cycle: AdmissionCycle;
  canEdit: boolean;
  /** Fields applicants have answered (open cycles). */
  answeredKeys?: string[];
  onChanged: (next: AdmissionCycle) => void;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * The cycle's application form at a glance — each section opens to its
 * questions — and Edit, which opens the form editor with a preview of what
 * families see.
 */
const ApplicationFormSection: React.FC<Props> = ({ cycle, canEdit, answeredKeys, onChanged }) => {
  const [editOpen, setEditOpen] = useState(false);
  const sections = activeFormSections(cycle.formTemplate?.sections);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const totalFields = sections.reduce((s, sec) => s + sec.fields.length, 0);
  const requiredFields = sections.reduce((s, sec) => s + sec.fields.filter(f => f.required).length, 0);

  const toggle = (key: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <SectionCard
      title="Sections & questions"
      description={`${plural(sections.length, 'section')}, ${plural(totalFields, 'question')}, ${requiredFields} required. Open a section to see its questions.`}
      canEdit={canEdit}
      editing={false}
      onEditToggle={(next) => { if (next) setEditOpen(true); }}
      onSave={() => undefined}
      onCancel={() => undefined}
    >
      <TableCard>
        {sections.map((sec, idx) => {
          const key = sec.key || `s-${idx}`;
          return <SectionRow key={key} section={sec} open={expanded.has(key)} onToggle={() => toggle(key)} />;
        })}
      </TableCard>

      {editOpen && (
        <FormEditorDialog
          cycle={cycle}
          answeredKeys={answeredKeys}
          onCancel={() => setEditOpen(false)}
          onSaved={(next) => { setEditOpen(false); onChanged(next); }}
        />
      )}
    </SectionCard>
  );
};

// ─── Section row (read-only, expandable) ───────────────────────────────────
const SectionRow: React.FC<{ section: AdmissionFormSection; open: boolean; onToggle: () => void }> = ({
  section, open, onToggle,
}) => {
  const fields = section.fields || [];
  return (
    <Box sx={{ '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' } }}>
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        sx={{
          all: 'unset', boxSizing: 'border-box', width: '100%', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
          '&:hover': { bgcolor: 'action.hover' },
          '&:focus-visible': { bgcolor: 'action.hover' },
        }}
      >
        <Typography variant="body2" fontWeight={500} sx={{ flex: 1, minWidth: 0 }}>{section.title}</Typography>
        <Typography variant="caption" color="text.secondary" sx={numSx}>{plural(fields.length, 'question')}</Typography>
        <ExpandMore
          fontSize="small"
          sx={{ color: 'text.secondary', transition: 'transform 160ms', transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 2, pb: 1.5 }}>
          {fields.length === 0 ? (
            <Typography variant="body2" color="text.disabled">No fields in this section.</Typography>
          ) : (
            <Stack spacing={0.25}>
              {fields.map((f, i) => <FieldRow key={f.key || `f-${i}`} field={f} />)}
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const FieldRow: React.FC<{ field: AdmissionField }> = ({ field }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 0.5 }}>
    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{field.label}</Typography>
    {field.required && (isPhotoField(field) ? (
      <Tooltip title="Required on the online form. The desk (walk-in) and bulk upload can add applicants without one.">
        <span><ToneChip label="Required online" tone="highlight" /></span>
      </Tooltip>
    ) : <ToneChip label="Required" tone="highlight" />)}
    {field.isSensitive && (
      <Tooltip title="Hidden from staff without permission to view sensitive details">
        <span><ToneChip label="Sensitive" tone="neutral" /></span>
      </Tooltip>
    )}
    {field.isSystem && (
      <Tooltip title="One of EduBook’s built-in fields: its wording and type are fixed.">
        <span><ToneChip label="Built-in" tone="neutral" /></span>
      </Tooltip>
    )}
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72, textAlign: 'right' }}>{humanize(field.type)}</Typography>
  </Stack>
);

export default ApplicationFormSection;
